import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components/Button';
import { Flex } from '../../../../components/Flex';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { WarningModal } from '../../../../components/WarningModal';
import { Modal } from '../../../../components/modal/Modal';
import { UpdateUserEmailNewEmailDto } from '../../../../model/user/dto/UpdateUserEmailNewEmailDto';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { userSliceSelectors } from '../../../../redux/user/user.selector';
import { userActions } from '../../../../redux/user/user.slice';
import { AtiraToast } from '../../../../utils/AtiraToast';
import { Regexes } from '../../../../utils/String';
import { onSetTypedCRUDErrors } from '../../../../utils/onSetTypedCRUDErrors';

type SettingsEmailChangeNewEmailModalProps = {
  open: boolean;
  onClose: VoidFunction;
  onSuccess: ATVoidFunction<string>;
};

export const SettingsEmailChangeNewEmailModal: React.FC<
  SettingsEmailChangeNewEmailModalProps
> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);

  const { t } = useTranslation();

  const { control, getValues, watch, handleSubmit, reset, setError } =
    useForm<UpdateUserEmailNewEmailDto>();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const isButtonDisabled = !watch('email');

  const onLocalClose = () => {
    setWarningModalVisible(false);
    onClose();
    reset({ email: '' });
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const email = getValues('email');

      await dispatch(
        userActions.updateUserEmailNewEmail({ userId, email }),
      ).unwrap();

      AtiraToast.success(t('settings.general.changeEmail.newEmail.success'));

      reset({ email: '' });

      onSuccess(email);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => setWarningModalVisible(true)}
        title={t('settings.general.changeEmail.newEmail.modal.title')}
      >
        <Flex width={'100%'} flexDirection="column" gap="m">
          <Text color="fgSecondary" align="center">
            {t('settings.general.changeEmail.newEmail.modal.description')}
          </Text>

          <Controller
            control={control}
            name="email"
            rules={{
              required: {
                value: true,
                message: t('common.this_field_required'),
              },
              pattern: {
                value: Regexes.EMAIL_REGEX,
                message: t('error.valid.email'),
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Input
                value={value}
                onChange={onChange}
                title={t(
                  'settings.general.changeEmail.newEmail.modal.input.title',
                )}
                required
                valid={!error}
                errorMessage={error?.message}
              />
            )}
          />

          <Button
            onClick={handleSubmit(onSubmit, (e) => console.log(e))}
            disabled={isButtonDisabled}
            height="2.3rem"
            loading={loading}
          >
            {t('settings.general.changeEmail.newEmail.modal.button')}
          </Button>
        </Flex>
      </Modal>
      <WarningModal
        description={t('common.closeWarningModal.description')}
        open={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        onConfirm={onLocalClose}
      />
    </>
  );
};
